import type { Answer, ReportResponse, ScoreValue } from '@dwa/shared';
import { brandingFor, maturityLevelName, termsFor } from '@dwa/shared';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Card, ErrorBanner, RiskBadge, Spinner } from '../components/ui';
import { useFramework } from '../hooks/useFramework';
import { useAssessmentStore } from '../state/assessmentStore';

const scoreLabel = (s: ScoreValue | undefined) =>
  s === undefined ? '-' : s === 'NA' ? 'N/A' : `${s} · ${maturityLevelName(s)}`;
const pct = (n: number | null) => (n === null ? '-' : `${n}%`);
// Maturity band colour for bars/values: low = coral, mid = sage, high = cyan.
const bandText = (n: number | null) =>
  n === null ? 'text-climb-teal-soft' : n >= 67 ? 'text-climb-cyan' : n >= 34 ? 'text-climb-sage' : 'text-climb-coral';
const bandBar = (n: number | null) =>
  n === null ? 'bg-climb-line-dark' : n >= 67 ? 'bg-climb-cyan' : n >= 34 ? 'bg-climb-sage' : 'bg-climb-coral';

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className={`text-3xl font-medium ${accent ? 'text-climb-coral' : 'text-climb-cyan'}`}>{value}</div>
      <div className="text-sm text-climb-teal-soft">{label}</div>
    </div>
  );
}

// `data` is supplied for a server-fetched shareable report (/report/:id);
// otherwise the report renders from the local assessment store (/report).
export function Report({ data }: { data?: ReportResponse } = {}) {
  const storeReport = useAssessmentStore((s) => s.report);
  const storePillars = useAssessmentStore((s) => s.pillars);
  const storeAnswers = useAssessmentStore((s) => s.answers);
  const storeFocus = useAssessmentStore((s) => s.focus);
  const fw = useFramework((data ? data.focus : storeFocus) ?? undefined);
  // Which pillar detail panels are expanded on screen (PDF shows all regardless).
  const [openPillars, setOpenPillars] = useState<Set<string>>(new Set());
  const togglePillar = (id: string) =>
    setOpenPillars((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expandPillar = (id: string) => setOpenPillars((s) => new Set(s).add(id));

  const report = data ? { contact: data.contact, summary: data.summary } : storeReport;
  const pillars = data ? data.selection.pillars : storePillars;
  const answers: Record<string, Answer> = data
    ? Object.fromEntries(data.answers.map((a) => [a.questionId, a]))
    : storeAnswers;

  if (!report) return <Navigate to="/" replace />;
  if (fw.isPending) return <Spinner label="Preparing your report…" />;
  if (fw.isError || !fw.data) return <ErrorBanner message="Couldn’t load the framework. Please retry." />;
  const framework = fw.data;
  const branding = brandingFor(framework);
  const terms = termsFor(framework);
  const vendorNoun = branding.vendor || 'data & AI';
  // Frameworks without an explicit remediation step fall back to their target state.
  const recommendationFor = (q: { remediation?: string; whatGoodLooksLike: string }) =>
    q.remediation ?? q.whatGoodLooksLike;
  const { contact, summary } = report;
  const warTitle = data ? data.warTitle : framework.title;
  const pillarName = (id: string) => framework.pillars.find((p) => p.id === id)?.name ?? id;
  const activePillars = framework.pillars.filter((p) => pillars.includes(p.id));

  // TL;DR: strongest / weakest assessed pillar by maturity.
  const ranked = summary.pillars
    .filter((p) => p.maturityPct !== null)
    .sort((a, b) => (b.maturityPct ?? 0) - (a.maturityPct ?? 0));
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];

  // Prioritized remediation: lowest-scored controls, weighted by risk.
  const RISK_WEIGHT: Record<string, number> = { High: 3, Medium: 2, Low: 1 };
  const remediations = framework.questions
    .filter((q) => pillars.includes(q.pillarId))
    .map((q) => ({ q, score: answers[q.id]?.score }))
    .filter(
      (x): x is { q: (typeof framework.questions)[number]; score: Exclude<ScoreValue, 'NA'> } =>
        typeof x.score === 'number' && x.score <= 2,
    )
    .sort((a, b) => RISK_WEIGHT[b.q.risk]! - RISK_WEIGHT[a.q.risk]! || a.score - b.score)
    .slice(0, 6);

  return (
    <div className="print-sheet space-y-8">
      <div className="no-print flex items-center justify-between">
        <h1 className="text-2xl font-medium text-climb-paper">Your report</h1>
        <Button onClick={() => window.print()}>Save as PDF</Button>
      </div>

      <header>
        <div className="flex items-start justify-between gap-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-climb-cyan">{warTitle}</div>
          {/* Screen shows the logo in the page header; the PDF hides that header,
              so brand the report top-right here for print, clickable to climb.ai. */}
          <a href="https://climb.ai" className="hidden print:block">
            <img src="/climb-logo-dark.svg" alt="Climb" className="h-7 w-auto" />
          </a>
        </div>
        <div className="mt-8">
          <h2 className="text-2xl font-medium text-climb-paper">{contact.company}</h2>
          <p className="text-sm text-climb-teal-soft">
            {contact.firstName} {contact.lastName} · {contact.email}
          </p>
        </div>
      </header>

      {/* Executive summary */}
      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-climb-cyan">Executive summary</h3>
        <p className="max-w-3xl leading-relaxed text-climb-paper/80">
          {contact.company} achieved an overall maturity of{' '}
          <span className="font-medium text-climb-cyan">{pct(summary.overall.maturityPct)}</span>:{' '}
          {pct(summary.core.maturityPct)} across the assessed {terms.coreGroupLabel.toLowerCase()} and{' '}
          {pct(summary.lenses.maturityPct)} across the {terms.lensGroupLabel.toLowerCase()}. The review identified{' '}
          <span className="font-medium text-climb-coral">
            {summary.overall.highRiskGaps} high-risk gap{summary.overall.highRiskGaps === 1 ? '' : 's'}
          </span>{' '}
          requiring prioritized remediation.
          {strongest && weakest
            ? ` ${pillarName(strongest.pillarId)} is the strongest area at ${pct(strongest.maturityPct)}, while ${pillarName(weakest.pillarId)} represents the greatest opportunity to improve at ${pct(weakest.maturityPct)}.`
            : ''}
        </p>
      </section>

      <Card className="no-print bg-climb-surface-2">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-lg font-medium text-climb-paper">Want help closing the gaps?</h3>
            <p className="text-sm text-climb-teal-soft">
              Climb ships enterprise {vendorNoun} outcomes in weeks, not quarters.
            </p>
          </div>
          <a
            href="https://climb.ai"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-lg bg-climb-coral px-5 py-2.5 font-medium text-white hover:bg-climb-coral-mid"
          >
            Book a discovery call
          </a>
        </div>
      </Card>

      <Card>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          <Stat label="Overall maturity" value={pct(summary.overall.maturityPct)} />
          <Stat label="Core maturity" value={pct(summary.core.maturityPct)} />
          <Stat label="Lenses maturity" value={pct(summary.lenses.maturityPct)} />
          <Stat label="High-risk gaps" value={String(summary.overall.highRiskGaps)} accent />
        </div>
      </Card>

      <section className="break-before-page">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-climb-cyan">By {terms.unit}</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {summary.pillars.map((p) => (
            <a
              key={p.pillarId}
              href={`#pillar-${p.pillarId}`}
              onClick={() => expandPillar(p.pillarId)}
              className="block break-inside-avoid"
            >
              <Card className="transition hover:border-climb-teal-soft">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-climb-paper">{pillarName(p.pillarId)}</h4>
                  <span className={`font-mono ${bandText(p.maturityPct)}`}>{pct(p.maturityPct)}</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-climb-line-dark">
                  <div className={`h-full ${bandBar(p.maturityPct)}`} style={{ width: `${p.maturityPct ?? 0}%` }} />
                </div>
                <p className="mt-2 text-xs text-climb-teal-soft">
                  {p.answered}/{p.total} answered · {p.highRiskGaps} high-risk gap{p.highRiskGaps === 1 ? '' : 's'}
                </p>
              </Card>
            </a>
          ))}
        </div>
      </section>

      {summary.highRiskGaps.length > 0 ? (
        <section id="gaps" className="scroll-mt-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-climb-coral">High-risk gaps</h3>
          {/* Boxed on screen; in print it can span pages, so drop the box. */}
          <Card className="print:rounded-none print:border-0 print:bg-transparent print:p-0">
            <ul className="space-y-5">
              {summary.highRiskGaps.map((g) => {
                const q = framework.questions.find((x) => x.id === g.questionId);
                const a = answers[g.questionId];
                return (
                  <li key={g.questionId} className="border-l-2 border-climb-coral pl-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-xs text-climb-teal-soft">{g.questionId}</span>
                      {q ? <RiskBadge risk={q.risk} /> : null}
                    </div>
                    <div className="mt-1 text-sm font-medium text-climb-paper">{g.question}</div>
                    {q ? (
                      <div className="mt-2 space-y-1.5 text-sm">
                        {q.intent ? (
                          <p>
                            <span className="font-semibold text-climb-cyan">Impact: </span>
                            <span className="text-climb-paper/80">{q.intent}</span>
                          </p>
                        ) : null}
                        <p>
                          <span className="font-semibold text-climb-cyan">Recommendation: </span>
                          <span className="text-climb-paper/80">{recommendationFor(q)}</span>
                        </p>
                        <p className="text-climb-paper/70">
                          <span className="font-semibold text-climb-teal-soft">Your notes: </span>
                          {a?.notes ? a.notes : 'None provided'}
                        </p>
                        {q.reference ? (
                          <p>
                            <span className="font-semibold text-climb-teal-soft">{branding.referenceLabel}: </span>
                            <span className="font-mono text-xs text-climb-teal-soft">{q.reference}</span>
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>
      ) : null}

      {remediations.length > 0 ? (
        <section>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-climb-cyan">
            Suggested remediation
          </h3>
          <Card>
            <ol className="remediation-list list-decimal space-y-2 pl-5 marker:font-medium marker:text-climb-cyan">
              {remediations.map(({ q }) => (
                <li key={q.id} className="text-sm text-climb-paper">
                  {recommendationFor(q)}
                  <span className="ml-1 text-xs text-climb-teal-soft">
                    ({pillarName(q.pillarId)} · {q.risk} risk)
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </section>
      ) : null}

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-climb-cyan">
          All responses &amp; context
        </h3>
        {activePillars.map((pillar, pillarIndex) => {
          const pillarQuestions = framework.questions.filter((q) => q.pillarId === pillar.id);
          const answeredCount = pillarQuestions.filter((q) => answers[q.id]).length;
          const isOpen = openPillars.has(pillar.id);
          return (
          <div
            key={pillar.id}
            id={`pillar-${pillar.id}`}
            className={`mb-3 scroll-mt-4 ${pillarIndex > 0 ? 'break-before-page' : ''}`}
          >
            <button
              type="button"
              onClick={() => togglePillar(pillar.id)}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-climb-line-dark bg-climb-surface px-4 py-3 text-left transition hover:border-climb-teal-soft print:border-0 print:bg-transparent print:px-0"
              aria-expanded={isOpen}
            >
              <h4 className="text-lg font-medium text-climb-paper">{pillar.name}</h4>
              <span className="shrink-0 text-sm text-climb-teal-soft">
                {answeredCount}/{pillarQuestions.length} answered
                <span className="no-print ml-2 text-climb-cyan">{isOpen ? '▲ Hide' : '▼ Show'}</span>
              </span>
            </button>
            <ul className={`pillar-body mt-3 space-y-4 ${isOpen ? '' : 'hidden'}`}>
              {pillarQuestions.map((q) => {
                  const a = answers[q.id];
                  return (
                    <li key={q.id} className="break-inside-avoid border-b border-climb-line-dark pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-mono text-xs text-climb-teal-soft">{q.id}</span>
                        <div className="flex items-center gap-2">
                          <RiskBadge risk={q.risk} />
                          <span className="rounded-md bg-climb-teal-mid/15 px-2 py-0.5 font-mono text-sm text-climb-cyan">
                            Score: {scoreLabel(a?.score)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 text-sm font-medium text-climb-paper">{q.question}</div>
                      <div className="mt-2 space-y-1 text-sm">
                        {q.intent ? (
                          <p>
                            <span className="font-semibold text-climb-cyan">Why it matters: </span>
                            <span className="text-climb-paper/80">{q.intent}</span>
                          </p>
                        ) : null}
                        <p>
                          <span className="font-semibold text-climb-cyan">Recommendation: </span>
                          <span className="text-climb-paper/80">{recommendationFor(q)}</span>
                        </p>
                        <p className="text-climb-paper/70">
                          <span className="font-semibold text-climb-teal-soft">Your notes: </span>
                          {a?.notes ? a.notes : 'None provided'}
                        </p>
                        {q.reference ? (
                          <p>
                            <span className="font-semibold text-climb-teal-soft">{branding.referenceLabel}: </span>
                            <span className="font-mono text-xs text-climb-teal-soft">{q.reference}</span>
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
          );
        })}
      </section>

      <Card className="no-print bg-climb-surface-2">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-lg font-medium text-climb-paper">Ready to act on these results?</h3>
            <p className="text-sm text-climb-teal-soft">
              Climb’s {vendorNoun}-native team turns this review into a prioritized plan and ships the fixes with you.
            </p>
          </div>
          <a
            href="https://climb.ai"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-lg bg-climb-coral px-5 py-2.5 font-medium text-white hover:bg-climb-coral-mid"
          >
            Talk to a Climb expert
          </a>
        </div>
      </Card>
    </div>
  );
}
